import { collection, addDoc, doc, setDoc, getDocs, serverTimestamp } from "firebase/firestore";
import { db } from "./firebaseConfig";

/**
 * Save user registration data.
 * If an agent ID is provided, also save the registration under that agent.
 */
export const saveUserData = async (userData) => {
  try {
    // Extract agentId if present
    const { agentId, coverDetailsOption, ...cleanedData } = userData;

    // Common data structure
    const commonData = {
      ...cleanedData,
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      phoneNumber: userData.phoneNumber,
      countryCode: userData.countryCode,
      dateOfBirth: userData.dateOfBirth || "",
      dateOfPayment: userData.dateOfPayment || "",
      payeeGhanaCardNumber: userData.payeeGhanaCardNumber,
      coverOption: userData.coverOption,
      nextofkin: userData.nextofkin,
      annualPremium: Number(userData.annualPremium),
      paymentMethod: userData.paymentMethod,
      paystackRef: userData.paystackRef,
      ageGroup: userData.ageGroup,
      beneficiaries: userData.beneficiaries || [],
      submittedAt: serverTimestamp(),
      status: "pending",
    };

    // Save user data to `users_new` collection
    const userRef = await addDoc(collection(db, "users_new"), commonData);
    console.log("User saved in users_new with ID:", userRef.id);

    // If registered by an agent, also save under `agents/{agentId}/registrations`
    if (agentId) {
      const agentRef = doc(db, "agents", agentId);
      const registrationRef = collection(agentRef, "registrations");
      await addDoc(registrationRef, {
        ...commonData,
        agentId,
        registeredAt: serverTimestamp(),
      });
      console.log("User also saved under agent:", agentId);
    }

    return userRef.id;
  } catch (error) {
    console.error("Error saving user data:", error);
    throw error;
  }
};

/**
 * Register a new agent.
 */
export const addAgent = async (agentData) => {
  try {
    const agentRef = doc(db, "agents", agentData.agentId); // Use UID as agentId
    await setDoc(agentRef, {
      fullName: agentData.fullName,
      email: agentData.email,
      phoneNumber: agentData.phoneNumber,
      createdAt: serverTimestamp(),
      totalRegistrations: 0, // Initialize agent tracking fields
      totalCommission: 0,
    });

    console.log("Agent registered with ID:", agentData.agentId);
    return agentData.agentId;
  } catch (error) {
    console.error("Error registering agent:", error);
    throw error;
  }
};

/**
 * Fetch all registrations linked to a specific agent.
 */
export const fetchAgentRegistrations = async (agentId) => {
  try {
    if (!agentId) return [];

    const agentRef = doc(db, "agents", agentId);
    const registrationsRef = collection(agentRef, "registrations");
    const snapshot = await getDocs(registrationsRef);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching agent registrations:", error);
    return [];
  }
};
